/**
 * Teams Management Hook
 * Provides state management and API integration for team operations
 * Handles fetching, filtering, pagination, and selection of teams
 */

import { useState, useEffect, useCallback } from "react";
import { teamsService } from "../services/teams-service";
import type {
  Team,
  TeamAgent,
  TeamQueryParams,
  TeamListState,
  TeamLoadingState,
} from "../types/team-types";

/**
 * Hook options for customizing behavior
 * Controls auto-fetch and initial query parameters
 */
interface UseTeamsOptions {
  /**
   * Auto-fetch teams on mount
   * Set to false to defer initial fetch
   */
  autoFetch?: boolean;

  /**
   * Initial query parameters
   * Applied to first fetch call
   */
  initialParams?: TeamQueryParams;
}

/**
 * Return type for useTeams hook
 * Provides state and methods for team management
 */
interface UseTeamsReturn {
  /**
   * Current team list state
   * Contains teams, pagination, and UI state
   */
  state: TeamListState;

  /**
   * Loading state for operations
   * Tracks async operation progress
   */
  loading: TeamLoadingState;

  /**
   * Fetch teams with optional parameters
   * Replaces current teams with new results
   */
  fetchTeams: (params?: TeamQueryParams) => Promise<void>;

  /**
   * Fetch teams enriched with recent chat IDs
   * Replaces current teams with backend-enriched results
   */
  fetchTeamsWithRecentChat: (params?: TeamQueryParams) => Promise<void>;

  /**
   * Refresh current teams list
   * Re-fetches with current parameters
   */
  refresh: () => Promise<void>;

  /**
   * Load more teams for pagination
   * Appends next page to current list
   */
  loadMore: () => Promise<void>;

  /**
   * Search teams by query string
   * Updates search filter and refetches
   */
  search: (query: string) => void;

  /**
   * Filter teams by given criteria
   * Updates filters and refetches
   */
  filter: (filters: TeamListState["filters"]) => void;

  /**
   * Select a team for detailed view
   * Updates selectedTeam in state
   */
  selectTeam: (team: Team | null) => void;

  /**
   * Reset state to initial values
   * Clears teams, filters, and selection
   */
  reset: () => void;
}

/**
 * Initial state for team list
 * Default values for all state properties
 */
const initialState: TeamListState = {
  teams: [],
  pagination: null,
  loading: false,
  error: null,
  searchQuery: "",
  filters: {},
  selectedTeam: null,
};

/**
 * Initial loading state
 * Default values for loading indicators
 */
const initialLoadingState: TeamLoadingState = {
  loading: false,
  error: null,
  retrying: false,
};

/**
 * Custom hook for managing teams list and operations
 * Provides comprehensive state management for team features
 *
 * @param options - Hook configuration options
 * @returns Object with state and methods for team management
 */
export function useTeams(options: UseTeamsOptions = {}): UseTeamsReturn {
  const { autoFetch = true, initialParams = {} } = options;

  /**
   * Main state for teams list
   * Contains teams data and UI state
   */
  const [state, setState] = useState<TeamListState>(initialState);

  /**
   * Loading state for async operations
   * Tracks progress of API calls
   */
  const [loading, setLoading] = useState<TeamLoadingState>(initialLoadingState);

  /**
   * Current query parameters
   * Tracks active filters and pagination
   */
  const [currentParams, setCurrentParams] =
    useState<TeamQueryParams>(initialParams);

  /**
   * Fetches teams from API with given parameters
   * Updates state with results or error
   *
   * @param params - Query parameters for filtering
   */
  const fetchTeams = useCallback(
    async (params?: TeamQueryParams) => {
      const queryParams = params ?? currentParams;

      setState((prev) => ({ ...prev, loading: true, error: null }));
      setLoading((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const { teams, pagination } = await teamsService.fetchTeams(queryParams);

        setState((prev) => ({
          ...prev,
          teams,
          pagination,
          loading: false,
          error: null,
        }));

        /**
         * Update current params if new params provided
         * Ensures subsequent refreshes use same params
         */
        if (params) {
          setCurrentParams(params);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch teams";

        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
        setLoading((prev) => ({
          ...prev,
          error: errorMessage,
        }));
      } finally {
        setLoading((prev) => ({ ...prev, loading: false }));
      }
    },
    [currentParams]
  );

  /**
   * Fetches teams with backend-provided recent chat information
   * Updates state with enriched team payload
   *
   * @param params - Query parameters for filtering
   */
  const fetchTeamsWithRecentChat = useCallback(
    async (params?: TeamQueryParams) => {
      const queryParams = params ?? currentParams;

      setState((prev) => ({ ...prev, loading: true, error: null }));
      setLoading((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const { teams, pagination } =
          await teamsService.fetchTeamsWithRecentChat(queryParams);

        setState((prev) => ({
          ...prev,
          teams,
          pagination,
          loading: false,
          error: null,
        }));

        if (params) {
          setCurrentParams(params);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch teams";

        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
        setLoading((prev) => ({
          ...prev,
          error: errorMessage,
        }));
      } finally {
        setLoading((prev) => ({ ...prev, loading: false }));
      }
    },
    [currentParams]
  );

  /**
   * Refreshes the current teams list
   * Uses current parameters to refetch
   */
  const refresh = useCallback(async () => {
    await fetchTeamsWithRecentChat(currentParams);
  }, [fetchTeamsWithRecentChat, currentParams]);

  /**
   * Loads more teams for pagination
   * Appends next page results to existing list
   */
  const loadMore = useCallback(async () => {
    /**
     * Skip if no more pages available
     * Prevents unnecessary API calls
     */
    if (!state.pagination?.hasNextPage) return;

    const nextOffset = state.pagination.page * state.pagination.limit;
    const params = { ...currentParams, offset: nextOffset };

    setLoading((prev) => ({ ...prev, loading: true }));

    try {
      const { teams, pagination } = await teamsService.fetchTeams(params);

      /**
       * Append new teams to existing list
       * Maintains scroll position for infinite scroll
       */
      setState((prev) => ({
        ...prev,
        teams: [...prev.teams, ...teams],
        pagination,
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to load more teams";
      setLoading((prev) => ({ ...prev, error: errorMessage }));
    } finally {
      setLoading((prev) => ({ ...prev, loading: false }));
    }
  }, [state.pagination, currentParams]);

  /**
   * Searches teams by query string
   * Resets pagination and filters by search term
   *
   * @param query - Search query string
   */
  const search = useCallback(
    (query: string) => {
      setState((prev) => ({ ...prev, searchQuery: query }));
      fetchTeams({ ...currentParams, search: query, offset: 0 });
    },
    [fetchTeams, currentParams]
  );

  /**
   * Filters teams by given criteria
   * Applies pattern and status filters
   *
   * @param filters - Filter criteria object
   */
  const filter = useCallback(
    (filters: TeamListState["filters"]) => {
      setState((prev) => ({ ...prev, filters }));
      fetchTeams({
        ...currentParams,
        pattern: filters.pattern,
        status: filters.status,
        offset: 0,
      });
    },
    [fetchTeams, currentParams]
  );

  /**
   * Selects a team for detailed view
   * Updates selectedTeam in state
   *
   * @param team - Team to select, or null to deselect
   */
  const selectTeam = useCallback((team: Team | null) => {
    setState((prev) => ({ ...prev, selectedTeam: team }));
  }, []);

  /**
   * Resets state to initial values
   * Clears all teams, filters, and selection
   */
  const reset = useCallback(() => {
    setState(initialState);
    setLoading(initialLoadingState);
    setCurrentParams(initialParams);
  }, [initialParams]);

  /**
   * Auto-fetch on mount if enabled
   * Performs initial data load
   */
  useEffect(() => {
    if (autoFetch) {
      fetchTeamsWithRecentChat(initialParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    loading,
    fetchTeams,
    fetchTeamsWithRecentChat,
    refresh,
    loadMore,
    search,
    filter,
    selectTeam,
    reset,
  };
}

/**
 * Return type for useTeam hook
 * Provides state and methods for single team management
 */
interface UseTeamReturn {
  /**
   * Team data with embedded agents
   */
  team: (Team & { agents: TeamAgent[] }) | null;

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error message if any
   */
  error: string | null;

  /**
   * Refresh team data
   */
  refresh: () => Promise<void>;
}

/**
 * Hook options for useTeam
 */
interface UseTeamOptions {
  /**
   * Auto-fetch team on mount
   */
  autoFetch?: boolean;
}

/**
 * Custom hook for fetching a single team by ID
 * Provides team data with agents
 *
 * @param teamId - Team ID to fetch, or null to skip
 * @param options - Hook configuration options
 * @returns Object with team state and refresh method
 */
export function useTeam(
  teamId: string | null,
  options: UseTeamOptions = {}
): UseTeamReturn {
  const { autoFetch = true } = options;

  const [team, setTeam] = useState<(Team & { agents: TeamAgent[] }) | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTeam = useCallback(async () => {
    if (!teamId) {
      setTeam(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await teamsService.fetchTeamById(teamId);
      setTeam(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch team";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  const refresh = useCallback(async () => {
    await fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    if (autoFetch && teamId) {
      fetchTeam();
    }
  }, [autoFetch, teamId, fetchTeam]);

  return {
    team,
    loading,
    error,
    refresh,
  };
}
